import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import axiosInstance from "../../axiosInstace";
import { Mail, ShieldCheck, ArrowRight } from "lucide-react";
import InputField from "../../components/InputComponent/InputField";
import { SD_PENDING_KEY } from "../../utils/selfDiscovery";

// Mounted at /self-discovery/continue. Reached ONLY right after a login
// that started from a self-discovery link (see getPostLoginRedirect in
// utils/selfDiscovery.js). Picks up what SelfDiscoveryGate stashed and
// finishes the flow:
//   PRIVATE -> OTP  (/api/user/test/validate, /api/user/test/verify-otp)
//   PUBLIC  -> common invite record validation
//              (/api/user/test/common/public/invite)
//
// Both calls now go through axiosInstance (authenticated, JWT attached)
// rather than plain axios, since the user has to log in first.
//
// PRIVATE flow field sourcing (confirmed):
//   - pending.linkId  -> decoded from the token (tokenUtils.js)
//   - userDetailsId   -> NOT in the token; comes from the logged-in user's
//                        /api/access response, i.e. `user.userDetailsId`
//                        from AuthContext.
//   - pending.testId  -> decoded from the token but currently unused here;
//                        the real testId used for navigation comes back
//                        from the verify-otp response instead.
export default function SelfDiscoveryContinue() {
  const { user, isLoading } = useAuth();
  const navigate = useNavigate();

  const [pending, setPending] = useState(null);
  const [step, setStep] = useState("loading");
  // loading | invalid | requestOtp | enterOtp | invitePending | error
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // Bounce back to login if this is ever loaded without an active session
  // (e.g. someone navigates here directly, or refreshes and the session
  // didn't restore).
  useEffect(() => {
    if (isLoading) return;
    if (!user) {
      navigate("/", { replace: true });
      return;
    }
    const raw = sessionStorage.getItem(SD_PENDING_KEY);
    if (!raw) {
      setStep("invalid");
      return;
    }
    try {
      const parsed = JSON.parse(raw);
      setPending(parsed);
      setStep(parsed.type === "PRIVATE" ? "requestOtp" : "invitePending");
    } catch {
      setStep("invalid");
    }
  }, [isLoading, user, navigate]);

  // Public flow: validate the common invite record as soon as we land here
  // — no extra user action needed, this just confirms the logged-in user
  // can take this test.
  useEffect(() => {
    if (step !== "invitePending" || !pending) return;

    const validatePublicInvite = async () => {
      setBusy(true);
      setError("");
      try {
        // ⚠️ ASSUMPTION — please confirm: the public token only carries one
        // numeric id (decoded here as `linkId`). This endpoint wants
        // `testId` + `userDetailsId`. Using pending.linkId as testId and
        // the logged-in user's id as userDetailsId until you confirm
        // otherwise. Also assumes the logged-in `user` object exposes
        // `userDetailsId` — adjust the field name below if it's called
        // something else in your `/api/access` response.
        const res = await axiosInstance.post(
          "/api/user/test/common/public/invite",
          null,
          {
            params: {
              testId: pending.linkId,
              userDetailsId: user.userDetailsId,
            },
          },
        );
        const { testId, sharedHistoryId } = res.data?.data || {};
        if (testId) {
          sessionStorage.removeItem(SD_PENDING_KEY);
          navigate(
            `/self-discovery/session/${testId}?sharedHistoryId=${sharedHistoryId || ""}`,
            { replace: true },
          );
        } else {
          setError("This test invite could not be validated.");
          setStep("error");
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "This test invite could not be validated.",
        );
        setStep("error");
      } finally {
        setBusy(false);
      }
    };

    validatePublicInvite();
  }, [step, pending, user, navigate]);

  const requestOtp = async () => {
    setError("");
    // userDetailsId comes from the logged-in user's /api/access response
    // (AuthContext), NOT from the token — the token only carries testId,
    // linkId, and email. See tokenUtils.js.
    if (!user?.userDetailsId) {
      setError(
        "Could not determine your account details. Please log in again.",
      );
      return;
    }
    setBusy(true);
    try {
      await axiosInstance.put("/api/user/test/validate", {
        userDetailsId: user.userDetailsId,
        linkId: pending.linkId,
      });
      setStep("enterOtp");
    } catch (err) {
      setError(
        err.response?.data?.message || "Could not send OTP. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (e) => {
    e.preventDefault();
    setError("");
    if (!otp) {
      setError("Enter the OTP sent to your email.");
      return;
    }
    setBusy(true);
    try {
      const res = await axiosInstance.post("/api/user/test/verify-otp", {
        linkId: pending.linkId,
        userDetailsId: user.userDetailsId,
        otp,
      });
      const { verified, testId, inviteId } = res.data?.data || {};
      if (verified && testId) {
        sessionStorage.removeItem(SD_PENDING_KEY);
        navigate(`/self-discovery/session/${testId}?inviteId=${inviteId}`, {
          replace: true,
        });
      } else {
        setError("That OTP didn't verify. Please check and try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "OTP verification failed.");
    } finally {
      setBusy(false);
    }
  };

  if (step === "loading" || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <p>Loading...</p>
      </div>
    );
  }

  if (step === "invalid") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white p-6 text-center">
        <p>
          We couldn't find a pending test link for this session. Please open
          your test link again.
        </p>
      </div>
    );
  }

  if (step === "invitePending") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black text-white">
        <p>Validating your invite...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-black">
      <div className="max-w-md w-full bg-white/10 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/20 p-8 text-white">
        <div className="text-center mb-6">
          <ShieldCheck className="w-10 h-10 mx-auto text-indigo-300 mb-2" />
          <h2 className="text-2xl font-bold">Verify your identity</h2>
          {pending?.email && (
            <p className="text-slate-300 mt-1">{pending.email}</p>
          )}
        </div>

        {error && (
          <div className="bg-red-50/10 border border-red-300/30 text-red-300 p-3 rounded-xl mb-4 text-center text-sm">
            {error}
          </div>
        )}

        {step === "requestOtp" && (
          <button
            onClick={requestOtp}
            disabled={busy}
            className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 py-3 rounded-xl font-medium flex items-center justify-center ${busy ? "opacity-70" : ""}`}
          >
            {busy ? "Sending OTP..." : "Send OTP to my email"}
            <ArrowRight className="ml-2 w-5 h-5" />
          </button>
        )}

        {step === "enterOtp" && (
          <form onSubmit={verifyOtp} className="space-y-4">
            <InputField
              label="Enter OTP"
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="6-digit code"
              icon={Mail}
            />
            <button
              type="submit"
              disabled={busy}
              className={`w-full bg-gradient-to-r from-indigo-500 to-blue-600 py-3 rounded-xl font-medium flex items-center justify-center ${busy ? "opacity-70" : ""}`}
            >
              {busy ? "Verifying..." : "Verify & Continue"}
              <ArrowRight className="ml-2 w-5 h-5" />
            </button>
            <button
              type="button"
              onClick={requestOtp}
              disabled={busy}
              className="text-indigo-300 text-sm w-full text-center"
            >
              Resend OTP
            </button>
          </form>
        )}

        {step === "error" && (
          <button
            onClick={() => window.location.reload()}
            className="w-full bg-white/10 border border-white/20 py-3 rounded-xl font-medium"
          >
            Try again
          </button>
        )}
      </div>
    </div>
  );
}
